import { formatDate } from "@angular/common";
import { Component, DestroyRef, inject, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { LoadingService } from "@coso/loading";
import { IS_SAAS, DialogComponent } from "@coso/utility";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { PageChangedEvent } from "ngx-bootstrap/pagination";
import { PaginatorComponent } from "@coso/grid";
import { ActionType, ILogHolder } from "../../../models/auto-generated";
import { HistoryLogsService } from "../../../services/history-logs.service";
import { combineDateWithTime } from "../../../utils/date";
import { handleError } from "../../../utils/handle-error";
import { saveAsExcel, saveAsZip } from "../../../utils/saveAs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subscription } from "rxjs";
import { FormArray, FormBuilder, FormGroup } from "@coso/forms";

export interface AdvancedFilterModel {
  dataSource: string;
  fromDate?: string;
  fromTime?: string;
  toDate?: string;
  toTime?: string;
  logTypes: ActionType[];
}

@Component({
  standalone:false,
  templateUrl: "./history-logs.component.html",
  styleUrls: ["./history-logs.component.scss"]
})
export class HistoryLogsComponent implements OnInit {
  LogType = ActionType;

  instanceId: string;
  errorMessage: string;

  logEntries: ILogHolder[];
  totalNumberOfItems: number;
  lastLogTimeStamp: Date;
  itemsPerPage = 10;  
  hasFixedRowCount = false;
  currentPage : number = 1;

  @ViewChild("advancedFilters", { static: true }) advancedFilters: TemplateRef<any>;
  @ViewChild(PaginatorComponent) paginator: PaginatorComponent;
  filtersFormGroup: FormGroup<AdvancedFilterModel>;
  private dataSource = "Database";
  private logTypes = [ActionType.INFO, ActionType.WARN, ActionType.EVENT, ActionType.ERROR, ActionType.PERMISSION_WRITE];
  private startDate: string;
  private endDate: string;
  private pageLastLogDate : string;
  private pageFirstLogDate : string;
  quickFilterText: string;
  filterErrorMessage : string;
  private destroyRef = inject(DestroyRef);
  private currentLoadSubscription: Subscription;
  
  get formArray() {
    return this.filtersFormGroup.controls["logTypes"] as FormArray;
  }

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private historyLogsService: HistoryLogsService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.params["instance-id"];
    const sourceParam = this.route.snapshot.queryParams["source"];
    if (sourceParam) this.dataSource = sourceParam;
    const logTypeParam = this.route.snapshot.queryParams["logType"];
    if (logTypeParam) this.logTypes = Array.from(logTypeParam, Number);
    this.loadHistoryLogs(1, this.itemsPerPage);
    this.filtersFormGroup = this.fb.group<AdvancedFilterModel>({
      dataSource: this.fb.control("Database"),
      fromDate: this.fb.control(undefined),
      fromTime: this.fb.control(undefined),
      toDate: this.fb.control(undefined),
      toTime: this.fb.control(undefined),
      logTypes: this.fb.array([this.fb.control(null), this.fb.control(null), this.fb.control(null), this.fb.control(null), this.fb.control(null), this.fb.control(null)])
    });
    this.filtersFormGroup.controls.fromDate.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.validateDateTimeFilters());
    this.filtersFormGroup.controls.toDate.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.validateDateTimeFilters());
  }

  reload(): void {
    this.paginator?.pagination?.selectPage(1);
    if (this.currentPage === 1) {
      this.loadHistoryLogs(1, this.itemsPerPage);
    }
  }

  onPaginationChanged(event: PageChangedEvent): void {
    let offset = event.itemsPerPage * (event.page - 1);
    this.pageLastLogDate = undefined;
    this.pageFirstLogDate = undefined;
    if(!this.isSaaS){
      this.loadHistoryLogs(event.page, event.itemsPerPage, offset, this.pageLastLogDate, this.pageFirstLogDate);
      return;
    }
    //Here remember logs are ordered by timestamp descending so index 0 is most recent log
    if (event.page < this.currentPage) {
      const dateToFormat = new Date(this.logEntries.at(-1)?.timeStamp);
      this.pageFirstLogDate = dateToFormat?.toISOString();
    }
    if (event.page > this.currentPage) {
      const dateToFormat = new Date(this.logEntries.at(0)?.timeStamp);
      this.pageLastLogDate = dateToFormat?.toISOString();
      offset = event.itemsPerPage * (event.page - this.currentPage);
    }
    this.loadHistoryLogs(event.page, event.itemsPerPage, offset, this.pageLastLogDate, this.pageFirstLogDate);
  }

  onQuickFilterChanged() {
    this.paginator?.pagination?.selectPage(1);
    if (this.currentPage === 1) {
      this.loadHistoryLogs(1, this.itemsPerPage);
    }
  }

  onQuickFilterClear() {
    this.quickFilterText = undefined;
    this.paginator?.pagination?.selectPage(1);
    if (this.currentPage === 1) {
      this.loadHistoryLogs(1, this.itemsPerPage);
    }
  }

  onPanelOpen() {
    const typesArray = [null, null, null, null, null, null]; // initializes array
    this.logTypes.forEach((type) => (typesArray[Number(type) - 1] = type));
    let fromDate = undefined;
    let fromTime = undefined;
    if (this.startDate) {
      const date = new Date(this.startDate);
      fromDate = formatDate(date, "yyyy-MM-dd", "en-US");
      fromTime = formatDate(date, "HH:mm", "en-US");
    }

    let toDate = undefined;
    let toTime = undefined;
    if (this.endDate) {
      const date = new Date(this.endDate);
      toDate = formatDate(date, "yyyy-MM-dd", "en-US");
      toTime = formatDate(date, "HH:mm", "en-US");
    }

    this.filtersFormGroup.patchValue({
      dataSource: this.dataSource,
      fromDate: fromDate,
      fromTime: fromTime,
      toDate: toDate,
      toTime: toTime,
      logTypes: typesArray
    });
    this.filtersFormGroup.updateValueAndValidity();
    this.filterErrorMessage = undefined;

    const modalRef = this.modalService.open(DialogComponent, { ariaLabelledBy: "modalTitle", keyboard: true, modalDialogClass: "modal-dialog-right" });
    modalRef.componentInstance.options = {
      titleText: "Advanced Filters",
      contentTemplateRef: this.advancedFilters,
      leftButtons: [{ label: "Apply", type: "submit", class: "btn btn-primary", form: "advancedFiltersForm", onClick: this.applyFilters.bind(this) }],
      rightButtons: [
        { label: "Reset", type: "button", class: "btn btn-danger mr-2", onClick: this.resetFilters.bind(this) },
        { label: "Cancel", type: "button", class: "btn btn-dark", onClick: this.closeFiltersPanel.bind(this) }
      ]
    };
  }

  private validateDateTimeFilters() : void {
    let fromDateTemp = new Date(this.filtersFormGroup.controls.fromDate.value);
    let endDateTemp = new Date(this.filtersFormGroup.controls.toDate.value);
    this.filterErrorMessage = undefined;
    if (!endDateTemp || !this.filtersFormGroup.controls.toDate.value) {
      endDateTemp = new Date();
    }
    if (fromDateTemp) {
      const msPerDay = 1000 * 60 * 60 * 24;
      let dayDifference = Math.round((endDateTemp.getTime() - fromDateTemp.getTime()) / msPerDay);
      if (dayDifference > 30) {
        //If its more than 30 days difference (our limit) we set the from date a few days foward so its a valid value
        fromDateTemp = new Date(fromDateTemp.getTime() + ((dayDifference - 30) * msPerDay));
        this.startDate = formatDate(fromDateTemp, "yyyy-MM-dd HH:mm", "en-US");
        this.filterErrorMessage = `The maximum allowed date range is 30 days. The Start Date will be adjusted accordingly. To ${formatDate(fromDateTemp, "MM-dd-yyyy", "en-US")}.`;
        this.filtersFormGroup.controls.fromDate.setValue(formatDate(fromDateTemp, "yyyy-MM-dd", "en-US"), { emitEvent: false });
      }
    }
  }

  private applyFilters(modalRef: NgbActiveModal) {
    modalRef.dismiss();
    const model = this.filtersFormGroup.getRawValue();
    this.dataSource = model.dataSource;
    if (model.fromDate) {
      const date = new Date(model.fromDate);
      const time = model.fromTime ?? "00:00";
      const dateTime = combineDateWithTime(date, time);
      this.startDate = formatDate(dateTime, "yyyy-MM-dd HH:mm ZZZZZ", "en-US");
    }
    if (model.toDate) {
      const date = new Date(model.toDate);
      const time = model.toTime ?? "23:59";
      const dateTime = combineDateWithTime(date, time);
      this.endDate = formatDate(dateTime, "yyyy-MM-dd HH:mm ZZZZZ", "en-US");
    }
    this.logTypes = model.logTypes.filter((type) => !!type);
    this.loadHistoryLogs(1, this.itemsPerPage);
  }

  private resetFilters(modalRef: NgbActiveModal) {
    modalRef.dismiss();
    this.dataSource = "Database";
    this.startDate = "";
    this.endDate = "";
    this.logTypes = [ActionType.INFO, ActionType.WARN, ActionType.EVENT, ActionType.ERROR, ActionType.PERMISSION_WRITE];
    this.loadHistoryLogs(1, this.itemsPerPage);
  }

  private closeFiltersPanel(modalRef: NgbActiveModal) {
    modalRef.dismiss();
  }

  exportLogsToZip(): void {
    this.loadingService.start("Exporting logs to Zip file");
    this.historyLogsService.exportAsZip(this.instanceId).subscribe(
      (data) => {
        saveAsZip(data);
        this.loadingService.stop();
      },
      (error) => {
        this.errorMessage = `Failed to export logs to Zip file due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  exportLogsToExcel(): void {
    this.loadingService.start("Exporting logs to Excel file");
    // check whether threshold works fine for SaaS
    this.historyLogsService.exportAsExcel(this.instanceId, 100000, 0, this.dataSource, this.logTypes, this.startDate, this.endDate, this.quickFilterText).subscribe(
      (data) => {
        saveAsExcel(data);
        this.loadingService.stop();
      },
      (error) => {
        this.errorMessage = `Failed to export logs to Excel file due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private loadHistoryLogs(page: number, pageSize: number, offset: number = undefined, pageLastLogDate : string = undefined, pageFirstLogDate : string = undefined) {
    this.errorMessage = undefined;
    this.currentLoadSubscription?.unsubscribe();
    this.loadingService.start("Loading history logs", "loadingHistoryLogs");
    this.itemsPerPage = pageSize;
    const offSetLogs = offset != undefined ? offset : pageSize * (page - 1);
    this.currentPage = page;
    this.currentLoadSubscription = this.historyLogsService.get(this.instanceId, pageSize, offSetLogs, this.dataSource, this.logTypes, this.startDate, this.endDate, this.quickFilterText, pageLastLogDate, pageFirstLogDate, pageSize * (page - 1)).subscribe(
      (data) => {
        this.logEntries = data.logs;        
        this.totalNumberOfItems = data.metadata.recordCount;                
        this.hasFixedRowCount = !data.metadata.hasMoreRecords;
        this.loadingService.stop("loadingHistoryLogs");
      },
      (error) => {
        this.errorMessage = `Failed to load history logs due to: ${handleError(error)}`;
        this.loadingService.stop("loadingHistoryLogs");
      }
    );
  }
}
